import { useState, useEffect, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitDecorationMuraleFeuillageOrnementsNoel() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
  const [quantity, setQuantity] = useState(1);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const [productImage, setProductImage] = useState<string>('https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/a60f77ede70772177c720322b4d56a62.png');

  useEffect(() => {
    isMountedRef.current = true;

    const loadImage = async () => {
      const image = await getProductImage('decoration-murale-feuillage-ornements-noel');
      if (image && isMountedRef.current) {
        setProductImage(image);
      }
    };

    loadImage();

    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const product = {
    id: 'decoration-murale-feuillage-ornements-noel',
    name: 'Décoration murale en livre plié – Feuillage & Ornements de Noël (pièce unique)',
    price: 30,
    image: productImage,
    category: 'Collection de Noël',
    subcategory: 'Décoration murale',
    description: 'Décoration murale artisanale en livre plié avec feuillage et ornements de Noël',
    stock: 1
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    if (!isMountedRef.current) return;

    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    setNotification({ show: true, message, type });

    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(prev => ({ ...prev, show: false }));
      }
    }, 3000);
  };

  const handleAddToCart = () => {
    if (!isMountedRef.current) return;

    try {
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          description: product.description
        });
      }

      showNotification(`${product.name} ajouté au panier !`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  const handleBuyNow = async () => {
    if (!isMountedRef.current) return;

    try {
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          description: product.description
        });
      }

      navigate('/panier');
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <main className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Section */}
            <section className="space-y-4">
              <div className="aspect-square bg-[#FAF9F8] rounded-2xl overflow-hidden border-2 border-[#B97A72] shadow-lg">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </section>

            {/* Product Info Section */}
            <section className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#C49B86] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Pièce unique ✨
                  </span>
                  <span className="bg-[#B97A72] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Collection de Noël
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.name}
                </h1>
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-4xl sm:text-5xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {product.price}€
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="bg-[#FAF9F8] rounded-xl p-6 border-2 border-[#EBD2CF]">
                <h2 className="text-2xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  ✨ Décoration murale en livre plié – Feuillage & Ornements de Noël (pièce unique)
                </h2>
                <div className="space-y-4 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p>
                    Cette décoration murale est une création artisanale réalisée à partir d'un livre entièrement plié à la main, formant une élégante base sculptée. Elle est sublimée par un assemblage raffiné de feuilles de houx, de branches de pin, de boules rouges et dorées, ainsi que d'un ruban doré.
                  </p>
                  <p>
                    Son style naturel, festif et élégant en fait une pièce forte pour habiller un mur pendant les fêtes de Noël ou pour compléter une décoration de table ou d'étagère.
                  </p>
                  <p>
                    Chaque création est unique, façonnée avec soin dans mon atelier à partir de livres recyclés. Aucune pièce ne peut être reproduite à l'identique.
                  </p>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold text-[#B97A72] mb-2">🎄 Idéale pour :</h3>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Une décoration murale de Noël chic et originale</li>
                      <li>Un cadeau artisanal d'exception</li>
                      <li>Apporter une touche naturelle, chaleureuse et festive à un intérieur</li>
                    </ul>
                  </div>

                  <div className="mt-6 space-y-2">
                    <p className="flex items-center">
                      <span className="mr-2">✅</span>
                      <strong>Création 100 % faite main</strong>
                    </p>
                    <p className="flex items-center">
                      <span className="mr-2">✅</span>
                      <strong>Pièce unique</strong>
                    </p>
                    <p className="flex items-center">
                      <span className="mr-2">✅</span>
                      <strong>Livre recyclé – démarche éco-responsable</strong>
                    </p>
                    <p className="flex items-center">
                      <span className="mr-2">✅</span>
                      <strong>Feuillage, boules décoratives et ruban inclus</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="bg-[#FAF9F8] rounded-xl p-6 border-2 border-[#EBD2CF] space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#B97A72] rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 bg-[#FAF9F8] hover:bg-[#EBD2CF] text-[#6B5E5A] font-bold transition-colors cursor-pointer"
                    >
                      -
                    </button>
                    <span className="px-6 py-2 bg-white text-[#6B5E5A] font-semibold">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 bg-[#FAF9F8] hover:bg-[#EBD2CF] text-[#6B5E5A] font-bold transition-colors cursor-pointer"
                    >
                      +
                    </button>
                  </div>
                  <span className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    ({product.stock} disponible)
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 bg-[#B97A72] text-white px-6 py-4 rounded-full hover:bg-[#C49B86] transition-colors font-semibold text-lg whitespace-nowrap cursor-pointer flex items-center justify-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Ajouter au panier
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="flex-1 bg-[#C49B86] text-white px-6 py-4 rounded-full hover:bg-[#B97A72] transition-colors font-semibold text-lg whitespace-nowrap cursor-pointer flex items-center justify-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <i className="ri-flashlight-line mr-2"></i>
                    Acheter maintenant
                  </button>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-[#EBD2CF] rounded-xl p-6 space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-truck-line text-2xl text-[#B97A72] mt-1"></i>
                  <div>
                    <h3 className="font-semibold text-[#6B5E5A] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Livraison soignée
                    </h3>
                    <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Emballage protecteur pour garantir une réception en parfait état
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-hand-heart-line text-2xl text-[#B97A72] mt-1"></i>
                  <div>
                    <h3 className="font-semibold text-[#6B5E5A] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Fait main avec amour
                    </h3>
                    <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Chaque pièce est unique et créée avec soin dans mon atelier
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-leaf-line text-2xl text-[#B97A72] mt-1"></i>
                  <div>
                    <h3 className="font-semibold text-[#6B5E5A] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Éco-responsable
                    </h3>
                    <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Réalisé à partir de livres recyclés
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
